import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/dashboard/DashboardLayout";
import DashboardHeader from "../components/dashboard/DashboardHeader";
import ProjectTabs from "../components/dashboard/ProjectTabs";
import InterviewTable from "../components/dashboard/InterviewTable";
import ProjectOverview from "../components/dashboard/ProjectOverview";
import TodayInterviewsBanner from "../components/dashboard/TodayInterviewsBanner";
import { ResizableChatSidebar } from "@/components/dashboard/ResizableChatSidebar";
import {
  ChatSidebarProvider,
  useChatSidebar,
} from "@/contexts/ChatSidebarContext";
import {
  authService,
  interviewService,
  isDemoMode,
} from "../services/serviceFactory";
import { Interview, Project } from "../services/interviewService";
import { Button } from "@/components/ui/button";
import { track } from "@vercel/analytics";
import {
  Archive,
  RefreshCw,
  Plus,
  MoreHorizontal,
  Folder,
  Users,
  Edit,
  Download,
} from "lucide-react";
import ProjectStatusControl from "../components/dashboard/ProjectStatusControl";
import { Badge } from "@/components/ui/badge";
import SyncCalendarButton from "@/components/dashboard/SyncCalendarButton";
import LastSyncIndicator from "@/components/dashboard/LastSyncIndicator";
import NewProjectModal from "@/components/dashboard/NewProjectModal";
import EditProjectModal from "@/components/dashboard/EditProjectModal";
import ProjectSharingModal from "@/components/dashboard/ProjectSharingModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/hooks/use-toast";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { useSessionMonitor } from "../hooks/useSessionMonitor";
import { transcriptService } from "@/services/transcriptService";

interface DashboardProps {
  userName: string;
}

// Dashboard component content (everything except the provider wrapper)
const DashboardContent: React.FC<DashboardProps> = ({ userName }) => {
  const {
    isOpen: chatSidebarOpen,
    width: chatSidebarWidth,
    setIsOpen: setChatSidebarOpen,
    setWidth: setChatSidebarWidth,
  } = useChatSidebar();
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [ownedProjects, setOwnedProjects] = useState<Project[]>([]);
  const [sharedProjects, setSharedProjects] = useState<Project[]>([]);
  const [archivedProjects, setArchivedProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null);
  const [recentlySyncedIds, setRecentlySyncedIds] = useState<string[]>([]);
  const [syncIndicatorKey, setSyncIndicatorKey] = useState(0);
  const [showProjectSharingModal, setShowProjectSharingModal] = useState(false);
  const [lastDataUpdate, setLastDataUpdate] = useState<Date>(new Date());
  const navigate = useNavigate();

  // Monitor session status to catch expiration before API calls fail
  useSessionMonitor();

  useEffect(() => {
    // Load projects when component mounts
    fetchAllProjects();
  }, []);

  useEffect(() => {
    // Load interviews when selected project changes
    fetchInterviews();

    // Clear recently synced IDs when switching projects
    setRecentlySyncedIds([]);
  }, [selectedProject]);

  // Update document title when current project changes
  useEffect(() => {
    const currentProject = getCurrentProject();
    const titlePrefix = isDemoMode() ? "Ivy Demo" : "Ivy";
    if (currentProject) {
      document.title = `${titlePrefix} - ${currentProject.name}`;
    } else {
      document.title = titlePrefix;
    }
  }, [selectedProject, ownedProjects, sharedProjects, archivedProjects]);

  // Auto-refresh: Poll for updates every 15 minutes when tab is visible
  useEffect(() => {
    if (!selectedProject || isLoading) return;

    const pollInterval = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchInterviews();
      }
    }, 900000); // 15 minutes

    return () => clearInterval(pollInterval);
  }, [selectedProject, isLoading]);

  // Auto-refresh: Fetch updates when user returns to the tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && selectedProject) {
        // Refresh if data is more than 20 seconds old
        const timeSinceUpdate = Date.now() - lastDataUpdate.getTime();
        if (timeSinceUpdate > 20000) {
          fetchInterviews();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [selectedProject, lastDataUpdate]);

  const fetchAllProjects = async () => {
    try {
      const [ownedData, sharedData, archivedData] = await Promise.all([
        interviewService.getOwnedProjects(),
        interviewService.getSharedProjects(),
        interviewService.getArchivedProjects(),
      ]);

      setOwnedProjects(ownedData);
      setSharedProjects(sharedData);
      setArchivedProjects(archivedData);

      // Set default selected project from owned projects first, then shared if no owned projects
      // Prefer active projects, but also include inactive (paused) projects
      const defaultProject =
        ownedData.find((p) => p.status === "active") ||
        ownedData.find((p) => p.status === "inactive") ||
        sharedData.find((p) => p.status === "active") ||
        sharedData.find((p) => p.status === "inactive");

      if (defaultProject && !selectedProject) {
        setSelectedProject(defaultProject.id);
      }

      // Always set loading to false after fetching projects
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setError("Failed to load projects. Please try again later.");
      setIsLoading(false); // Also set loading to false on error
    }
  };

  const fetchInterviews = async () => {
    if (!selectedProject) return;

    setIsLoading(true);
    try {
      const interviewsData = await interviewService.getInterviewsByProject(
        selectedProject
      );
      setInterviews(interviewsData);
      setError(null);
      setLastDataUpdate(new Date());
    } catch (error) {
      console.error("Error fetching interviews:", error);
      setError("Failed to load interviews. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleProjectChange = (projectId) => {
    setSelectedProject(projectId);
  };

  const handleToggleProjectStatus = async (projectId, newStatus) => {
    try {
      const success = await interviewService.toggleProjectStatus(
        projectId,
        newStatus
      );

      if (success) {
        // Refresh all project categories
        await fetchAllProjects();

        // If the toggled project was the selected one and it was archived,
        // select another active or inactive project
        if (projectId === selectedProject && newStatus === "archived") {
          const defaultProject = [...ownedProjects, ...sharedProjects].find(
            (p) =>
              p.id !== projectId &&
              (p.status === "active" || p.status === "inactive")
          );

          if (defaultProject) {
            setSelectedProject(defaultProject.id);
          }
        }
      }
    } catch (error) {
      console.error("Error toggling project status:", error);
      setError("Failed to update project status. Please try again.");
    }
  };

  const handleAddProject = () => {
    setShowNewProjectModal(true);
  };

  const handleCreateProject = async (projectData) => {
    try {
      const newProject = await interviewService.createProject(projectData);
      if (newProject) {
        // Track project creation
        track("project_created", {
          status: projectData.status,
          has_budget: !!projectData.max_budget,
          has_target: !!projectData.target_n,
          has_client: !!projectData.client,
        });

        await fetchAllProjects();
        setSelectedProject(newProject.id); // Back to using id since service transforms the response
        setShowNewProjectModal(false);

        // Explicitly fetch interviews for the newly created project
        // This ensures the interview table is populated immediately
        setIsLoading(true);
        try {
          const interviewsData = await interviewService.getInterviewsByProject(
            newProject.id // Back to using id since service transforms the response
          );
          setInterviews(interviewsData);
          setError(null);
        } catch (error) {
          console.error("Error fetching interviews for new project:", error);
          setError("Failed to load interviews for the new project.");
        } finally {
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error("Error creating project:", error);
      // Check if the error is about having an active project already
      if (
        error.response &&
        error.response.data &&
        error.response.data.error &&
        error.response.data.error.includes("active project")
      ) {
        setError(
          "You can only have one active project at a time. Please set your new project as inactive."
        );
      } else {
        setError("Failed to create new project. Please try again.");
      }
    }
  };

  const handleSignOut = async () => {
    try {
      await authService.signOut();
      navigate("/signin");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Handle interview updates
  const handleInterviewUpdate = async (updatedInterviews) => {
    try {
      const result = await interviewService.updateInterviews(updatedInterviews);

      // Handle both old boolean return and new object return
      const isSuccess = typeof result === "boolean" ? result : result?.success;
      const errorMessage =
        typeof result === "object" && result?.error
          ? result.error
          : "An unexpected error occurred while saving.";

      if (isSuccess) {
        // ✅ Merge the updated interviews back into the full interviews array
        setInterviews((prevInterviews) => {
          const updatedMap = new Map<string, Interview>(
            updatedInterviews.map((interview) => [interview.id, interview])
          );

          // Start with existing interviews, updating any that are in the updatedMap
          const mergedInterviews = prevInterviews.map((interview) => {
            const updatedInterview = updatedMap.get(interview.id);
            return updatedInterview || interview;
          });

          // Add any new interviews that weren't in the original list
          const existingIds = new Set(
            prevInterviews.map((interview) => interview.id)
          );
          const newInterviews = updatedInterviews.filter(
            (interview) => !existingIds.has(interview.id)
          );

          return [...mergedInterviews, ...newInterviews];
        });

        // Clear recently synced IDs when user saves
        setRecentlySyncedIds([]);

        return { success: true };
      } else {
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error("Error updating interviews:", error);
      return {
        success: false,
        error: "An unexpected error occurred while saving.",
      };
    }
  };

  // Handle adding a secondary report expense
  const handleAddExpense = async (expenseData: Partial<Interview>) => {
    try {
      const newExpense = await interviewService.createInterview(
        expenseData,
        selectedProject
      );

      if (newExpense) {
        setInterviews((prev) => [...prev, newExpense]);
        setLastDataUpdate(new Date());
        toast({
          title: "Success",
          description: "Secondary report expense added successfully.",
        });

        // Track analytics
        track("secondary_report_added", {
          project_id: selectedProject,
          cost: expenseData.initialInterviewCost,
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to add secondary report expense.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error adding expense:", error);
      toast({
        title: "Error",
        description: "An error occurred while adding the expense.",
        variant: "destructive",
      });
    }
  };

  // Handle deleting a secondary report expense
  const handleDeleteExpense = async (expenseId: string) => {
    try {
      const success = await interviewService.deleteInterview(expenseId);

      if (success) {
        setInterviews((prev) => prev.filter((i) => i.id !== expenseId));
        setLastDataUpdate(new Date());
        toast({
          title: "Success",
          description: "Secondary report expense deleted.",
        });

        // Track analytics
        track("secondary_report_deleted", {
          project_id: selectedProject,
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to delete secondary report expense.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting expense:", error);
      toast({
        title: "Error",
        description: "An error occurred while deleting the expense.",
        variant: "destructive",
      });
    }
  };

  // Handle calendar sync completion
  const handleSyncComplete = async () => {
    // Capture current interview IDs before sync
    const currentInterviewIds = interviews.map((interview) => interview.id);

    // Fetch updated interviews after sync
    await fetchInterviews();

    // Force refresh of sync indicator
    setSyncIndicatorKey((prev) => prev + 1);

    // After state is updated, we need to compare with the new list
    // We'll do this in a separate effect or get the new interviews directly
    try {
      const updatedInterviews = await interviewService.getInterviewsByProject(
        selectedProject
      );

      // Find newly added interviews (IDs that weren't in the original list)
      const newInterviewIds = updatedInterviews
        .map((interview) => interview.id)
        .filter((id) => !currentInterviewIds.includes(id));

      // Mark new interviews as recently synced
      if (newInterviewIds.length > 0) {
        setRecentlySyncedIds(newInterviewIds);
      }
    } catch (error) {
      console.error("Error detecting synced interviews:", error);
    }
  };

  const handleEditProject = (project: Project) => {
    setProjectToEdit(project);
    setShowEditProjectModal(true);
  };

  const handleUpdateProject = async (projectId: string, projectData) => {
    try {
      const updatedProject = await interviewService.updateProject(
        projectId,
        projectData
      );
      if (updatedProject) {
        await fetchAllProjects();
        setShowEditProjectModal(false);

        // Show success toast
        toast({
          title: "Project Updated",
          description: "Project details have been successfully updated.",
          variant: "default",
        });

        // If the status was changed and there was an active project conflict
        if (
          projectData.status === "inactive" &&
          getCurrentProject()?.id === projectId
        ) {
          // Find a new active project to select
          const activeProject = ownedProjects.find(
            (p) => p.status === "active" && p.id !== projectId
          );
          if (activeProject) {
            setSelectedProject(activeProject.id);
          }
        }
      }
    } catch (error) {
      console.error("Error updating project:", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.error &&
        error.response.data.error.includes("active project")
      ) {
        setError(
          "You can only have one active project at a time. Your project has been set as inactive."
        );
      } else {
        setError("Failed to update project. Please try again.");
      }
    }
  };

  const handleDownloadTranscripts = async () => {
    const currentProject = getCurrentProject();

    if (!currentProject) {
      toast({
        title: "Error",
        description: "No project selected. Please select a project first.",
        variant: "destructive",
      });
      return;
    }

    // Check if there are any transcripts available
    const hasTranscripts = interviews.some(
      (interview) => interview.transcriptStatus === "ready"
    );

    if (!hasTranscripts) {
      toast({
        title: "No Transcripts Available",
        description:
          "There are no ready transcripts to download for this project.",
        variant: "destructive",
      });
      return;
    }

    try {
      await transcriptService.downloadProjectTranscripts(currentProject.id);

      toast({
        title: "Download Started",
        description:
          "Your transcript DOCX file is being generated and downloaded.",
      });
    } catch (error: any) {
      console.error("Error downloading transcripts:", error);
      toast({
        title: "Download Failed",
        description:
          error.message || "Failed to download transcripts. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleShareProject = (project: Project) => {
    setProjectToEdit(project); // Use projectToEdit for sharing modal
    setShowProjectSharingModal(true);
  };

  // Render project tabs for the sidebar
  const renderProjectTabs = () => (
    <ProjectTabs
      ownedProjects={ownedProjects}
      sharedProjects={sharedProjects}
      archivedProjects={archivedProjects}
      selectedProject={selectedProject}
      onProjectChange={handleProjectChange}
      onToggleProjectStatus={handleToggleProjectStatus}
      onAddProject={handleAddProject}
    />
  );

  // Get current project from any category
  const getCurrentProject = () => {
    return (
      ownedProjects.find((p) => p.id === selectedProject) ||
      sharedProjects.find((p) => p.id === selectedProject) ||
      archivedProjects.find((p) => p.id === selectedProject)
    );
  };

  // Format time since last update
  const formatTimeSince = (date: Date) => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  // Determine if current project is archived
  const isCurrentProjectArchived = () => {
    const currentProject = getCurrentProject();
    return currentProject?.status === "archived";
  };

  // Check if user has any active or inactive (paused) projects
  const hasActiveProjects = () => {
    return (
      ownedProjects.some(
        (p) => p.status === "active" || p.status === "inactive"
      ) ||
      sharedProjects.some(
        (p) => p.status === "active" || p.status === "inactive"
      )
    );
  };

  // Check if user has any projects at all (including archived)
  const hasAnyProjects = () => {
    return (
      ownedProjects.length > 0 ||
      sharedProjects.length > 0 ||
      archivedProjects.length > 0
    );
  };

  // Render the header component
  const renderHeader = () => {
    const currentProject = getCurrentProject();
    const canSync =
      currentProject &&
      currentProject.role === "owner" &&
      currentProject.status === "active";
    return (
      <header className="flex items-center justify-between h-12 px-3 sm:px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 shadow-lg">
        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
          <h1 className="text-lg sm:text-xl font-bold whitespace-nowrap flex items-center">
            <span className="text-ivy-green bg-gradient-to-r from-ivy-green to-ivy-emerald bg-clip-text text-transparent">
              Ivy
            </span>

            <div className="ml-2 w-2 h-2 bg-ivy-green rounded-full animate-pulse"></div>
          </h1>
          {isDemoMode() && (
            <Badge
              variant="secondary"
              className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs hidden sm:inline-flex"
            >
              DEMO MODE
            </Badge>
          )}
        </div>
        <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
          <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden md:inline whitespace-nowrap">
            Welcome, {userName}
          </span>
          <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 md:hidden whitespace-nowrap">
            {userName}
          </span>
          <div className="hidden sm:flex">
            <LastSyncIndicator
              key={syncIndicatorKey}
              onSyncUpdate={fetchInterviews}
            />
          </div>
          <div className="hidden sm:flex">
            <SyncCalendarButton
              onSyncComplete={handleSyncComplete}
              disabledExternally={!canSync}
            />
          </div>
          <ThemeToggle />
          <button
            className="px-2 sm:px-4 py-1 text-xs sm:text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-md transition-colors whitespace-nowrap"
            onClick={handleSignOut}
          >
            {isDemoMode() ? "Exit" : "Sign Out"}
          </button>
        </div>
      </header>
    );
  };

  // Render the right sidebar
  const renderRightSidebar = () => {
    if (!selectedProject) return null;

    return (
      <ResizableChatSidebar
        projectId={selectedProject}
        interviews={interviews}
        isOpen={chatSidebarOpen}
        onToggle={setChatSidebarOpen}
        width={chatSidebarWidth}
        onWidthChange={setChatSidebarWidth}
      />
    );
  };

  return (
    <DashboardLayout
      sidebar={renderProjectTabs()}
      header={renderHeader()}
      rightSidebar={renderRightSidebar()}
    >
      {/* Demo Notice */}
      {isDemoMode() && (
        <div className="p-4 mb-6 text-sm text-blue-700 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/50 border border-blue-200 dark:border-blue-700 rounded-md">
          <p className="font-medium">You're in Demo Mode!</p>
          <p className="mt-1">
            This is an illustrative demo showing how the product works with
            sample data. The core workflow including calendar sync, interview
            management, and AI features are fully functional. However, some
            features like project editing and archiving are not available in the
            demo environment.
          </p>
        </div>
      )}

      {error && (
        <div className="p-4 mb-6 text-sm text-red-700 dark:text-red-200 bg-red-100 dark:bg-red-900/50 rounded-md">
          {error}
        </div>
      )}

      {/* No Projects State */}
      {!hasActiveProjects() && !selectedProject && !isLoading ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-md">
            <div className="mb-4">
              <Folder className="mx-auto h-16 w-16 text-gray-300 dark:text-gray-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No Active Projects
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You don't have any active projects. Create a project to start
              tracking interviews and managing your research.
            </p>
            <Button
              onClick={handleAddProject}
              className="bg-ivy-green hover:bg-ivy-emerald text-white px-6 py-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Project
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-start mb-6">
            <div>
              <div className="flex items-center">
                <h1 className="text-2xl font-bold tracking-tight mr-2 text-gray-900 dark:text-gray-100">
                  {getCurrentProject()?.name}
                </h1>

                {/* User Role Badge */}
                {getCurrentProject()?.role && (
                  <Badge
                    variant="outline"
                    className={`ml-2 ${
                      getCurrentProject()?.role === "owner"
                        ? "bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700"
                        : getCurrentProject()?.role === "editor"
                        ? "bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700"
                        : "bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600"
                    }`}
                  >
                    {getCurrentProject()?.role === "owner"
                      ? "Owner"
                      : getCurrentProject()?.role === "editor"
                      ? "Editor"
                      : "Viewer"}
                  </Badge>
                )}

                {/* Project Actions Menu */}
                {getCurrentProject() &&
                  (getCurrentProject()?.role === "owner" ||
                    getCurrentProject()?.role === "editor") && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 ml-2"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() =>
                            handleEditProject(getCurrentProject()!)
                          }
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Project Info
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleShareProject(getCurrentProject()!)
                          }
                        >
                          <Users className="w-4 h-4 mr-2" />
                          Share Project
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={handleDownloadTranscripts}
                          disabled={
                            !interviews.some(
                              (interview) =>
                                interview.transcriptStatus === "ready"
                            )
                          }
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Transcripts
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
              </div>

              {getCurrentProject()?.description && (
                <p className="text-muted-foreground mt-1">
                  {getCurrentProject()?.description}
                </p>
              )}
            </div>

            {getCurrentProject() && (
              <ProjectStatusControl
                status={getCurrentProject()?.status || "active"}
                onChangeStatus={(newStatus) =>
                  handleToggleProjectStatus(selectedProject, newStatus)
                }
                isOwner={getCurrentProject()?.role === "owner"}
              />
            )}
          </div>

          {/* Project overview with stats and metrics */}
          <ProjectOverview
            interviews={isLoading ? [] : interviews}
            currentProject={getCurrentProject()}
            recentlySyncedIds={recentlySyncedIds}
            onAddExpense={handleAddExpense}
            onDeleteExpense={handleDeleteExpense}
            currentUserRole={getCurrentProject()?.role || "viewer"}
          />

          {/* Today's interviews banner */}
          <TodayInterviewsBanner interviews={isLoading ? [] : interviews} />

          {/* Interview count indicator */}
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {!isLoading && (
                <>
                  Showing{" "}
                  <span className="font-medium">{interviews.length}</span>{" "}
                  interviews
                  {selectedProject !== "all" && (
                    <span> for {getCurrentProject()?.name}</span>
                  )}
                </>
              )}
            </div>
            {!isLoading && interviews.length > 0 && (
              <div className="text-xs text-muted-foreground">
                Updated {formatTimeSince(lastDataUpdate)}
              </div>
            )}
          </div>

          {/* Interview data table */}
          <InterviewTable
            interviews={interviews}
            isLoading={isLoading}
            onInterviewUpdate={handleInterviewUpdate}
            onRefresh={fetchInterviews}
            projectId={selectedProject}
            recentlySyncedIds={recentlySyncedIds}
            currentUserRole={getCurrentProject()?.role || "viewer"}
            onOpenChat={(interviewId) => {
              setChatSidebarOpen(true);
            }}
          />
        </>
      )}

      {/* Project Modals */}
      <NewProjectModal
        isOpen={showNewProjectModal}
        onClose={() => setShowNewProjectModal(false)}
        onSubmit={handleCreateProject}
        hasActiveProject={ownedProjects.some((p) => p.status === "active")}
      />

      <EditProjectModal
        isOpen={showEditProjectModal}
        onClose={() => setShowEditProjectModal(false)}
        onSubmit={handleUpdateProject}
        project={projectToEdit}
        hasActiveProject={ownedProjects.some(
          (p) => p.id !== projectToEdit?.id && p.status === "active"
        )}
      />

      <ProjectSharingModal
        isOpen={showProjectSharingModal}
        onClose={() => setShowProjectSharingModal(false)}
        project={projectToEdit}
        currentUserRole={projectToEdit?.role || "viewer"}
      />
    </DashboardLayout>
  );
};

// Main Dashboard component with provider
const Dashboard: React.FC<DashboardProps> = ({ userName }) => {
  // Add dashboard-mode class to body when component mounts
  useEffect(() => {
    document.body.classList.add("dashboard-mode");

    // Cleanup: remove class when component unmounts
    return () => {
      document.body.classList.remove("dashboard-mode");
    };
  }, []);

  return (
    <ChatSidebarProvider>
      <DashboardContent userName={userName} />
    </ChatSidebarProvider>
  );
};

export default Dashboard;
